import { Controller } from "@hotwired/stimulus"

// Renders the Cloudflare Turnstile widget explicitly instead of leaving it to
// the script's implicit scan for .cf-turnstile elements.
//
// The widget lives inside turbo_frame_tag "registration_form". The implicit
// scan runs once, when api.js loads, so when Turbo swaps that frame after a
// validation error the fresh markup is never scanned: no token is minted, and
// every retry fails server-side verification no matter what the member types.
// The abandoned widget is also what logs "Cannot find Widget cf-chl-widget-..."
// -- 170 of those in 30 days.
//
// Rendering on connect and removing on disconnect ties the widget's life to the
// element's, so a frame swap gets a working widget every time.
export default class extends Controller {
  static values = {
    sitekey: String,
    theme: { type: String, default: "light" }
  }

  connect() {
    if (!this.sitekeyValue) return

    this.whenScriptReady(() => this.renderWidget())
  }

  disconnect() {
    this.stopWaiting()

    // turnstile.remove throws if the widget is already gone -- which it is
    // whenever Turbo detached the frame before this ran. Tearing down is
    // best-effort; a throw here would abort the rest of Stimulus's teardown.
    if (this.widgetId !== undefined && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId)
      } catch (error) {
        console.debug("[turnstile] widget already removed", error)
      }
    }
    this.widgetId = undefined
  }

  renderWidget() {
    if (this.widgetId !== undefined) return

    // Turbo caches the page with the widget already rendered into this
    // container, so a restored snapshot arrives holding a dead widget: markup
    // whose iframe belongs to a document that no longer exists. Rendering on
    // top of it would leave two widgets in one container, the orphan still
    // reaching for a parent it cannot see. Start from an empty element every
    // time -- Cloudflare owns everything inside it.
    this.element.replaceChildren()

    this.widgetId = window.turnstile.render(this.element, {
      sitekey: this.sitekeyValue,
      theme: this.themeValue
    })
  }

  // api.js is loaded async defer, so on a cold page load it may not have run by
  // the time this controller connects. On a Turbo frame swap it already has.
  whenScriptReady(callback) {
    if (window.turnstile) {
      callback()
      return
    }

    this.readyTimer = setInterval(() => {
      if (!window.turnstile) return

      this.stopWaiting()
      callback()
    }, 100)
  }

  stopWaiting() {
    if (this.readyTimer) clearInterval(this.readyTimer)
    this.readyTimer = undefined
  }
}
